import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus, Search, Eye, Pencil, Trash2, FolderKanban, AlertCircle,
} from 'lucide-react';

import { toast } from 'react-toastify';

import { Button, Skeleton } from '../../ui-v2';
import {
  ToggleGroup, ToggleItem, Input, Select, Table, Pagination, EmptyState, Badge, ProgressBar,
} from '../../ui-v2.5';
import ConfirmacionEliminar from '../../ui/ConfirmacionEliminar';
import AccionesCampanaCancelada from '../campanas/AccionesCampanaCancelada';
import {
  ESTADOS_LISTA, MAX_BUSQUEDA, leerFiltrosLista, paramsDeFiltrosLista, permisosLista, rutaFila, rutaEdicion,
  siguienteOrden, paramsDeOrden,
} from '../campanas/listaCampanas';
import { ESTADO_CAMPANA, etiquetaDe } from '../compartidos/etiquetasEstado';
import { UsuarioContext } from '../../../providers/personaContext';
import {
  getCampanas, deleteCampana, cambiarEstadoCampana,
  contarParticipacionesCanceladasSistema,
} from '../api';

/**
 * ListaCampanas — rediseño (Fase 4), DS v2.5.
 *
 * - Filtros: los 6 estados en un control segmentado y el buscador (350 ms de espera). Estado,
 *   búsqueda y orden viven en la URL (?estado=&q=&orden=&dir=): se puede recargar o compartir.
 * - Orden por columnas (Campaña, Periodo, Progreso, Estado): lo hace el SERVIDOR, porque la lista
 *   está paginada allí; aquí solo se pide (paramsDeOrden). Sin orden, el del servidor por defecto.
 * - Tabla a todo el ancho con el mismo clic en la fila (Borrador al asistente; el resto, al
 *   resumen), también con el teclado (Tab y Enter). En pantallas estrechas, tarjetas.
 * - Cada acción solo se enseña a quien puede hacerla (permisosLista); la barrera es el servidor.
 * - Misma API, mismo modal de borrar y AccionesCampanaCancelada (aquí, como iconos).
 */
const PAGE_SIZE = 10;
const ESPERA_BUSQUEDA_MS = 350;

// El orden en móvil (sin cabeceras que pulsar): un desplegable con las mismas opciones.
const ORDENES_MOVIL = [
  { value: '', label: 'Por defecto' },
  { value: 'titulo:asc', label: 'Campaña (A–Z)' },
  { value: 'titulo:desc', label: 'Campaña (Z–A)' },
  { value: 'periodo:asc', label: 'Fecha límite más próxima' },
  { value: 'periodo:desc', label: 'Fecha límite más lejana' },
  { value: 'progreso:desc', label: 'Más avance' },
  { value: 'progreso:asc', label: 'Menos avance' },
  { value: 'estado:asc', label: 'Estado' },
];

function formatPeriodo(inicio, fin) {
  const opts = { day: '2-digit', month: 'short' };
  if (!inicio && !fin) return '—';
  if (!inicio && fin) return `Hasta ${new Date(fin).toLocaleDateString('es-ES', opts)}`;
  if (inicio && !fin) return `Desde ${new Date(inicio).toLocaleDateString('es-ES', opts)}`;
  const a = new Date(inicio).toLocaleDateString('es-ES', opts);
  const b = new Date(fin).toLocaleDateString('es-ES', opts);
  return `${a} – ${b}`;
}

export default function ListaCampanas() {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { estado, q, orden, dir } = leerFiltrosLista(sp);
  const { persona, tienePermiso } = useContext(UsuarioContext);
  const permisosDe = (c) => permisosLista({ rol: persona?.rol, tienePermiso, estado: c?.estado });
  const general = permisosDe(null);

  // El buscador se escribe aquí y va a la URL tras la espera; si la URL cambia por fuera (atrás,
  // «Quitar filtros»), el campo la sigue.
  const [texto, setTexto] = useState(q);
  useEffect(() => { setTexto((t) => (t.trim() === q ? t : q)); }, [q]);
  useEffect(() => {
    if (texto.trim() === q) return undefined;
    const t = setTimeout(() => setSp(paramsDeFiltrosLista(sp, { estado, q: texto }), { replace: true }), ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(t);
  }, [texto, q, estado, sp, setSp]);

  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [estado, q, orden, dir]);

  const [campanas, setCampanas] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [borrarTarget, setBorrarTarget] = useState(null);
  const [borrarError, setBorrarError] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = { page, limit: PAGE_SIZE, ...paramsDeOrden({ orden, dir }) };
      if (estado) params.estado = estado;
      if (q) params.q = q;
      const res = await getCampanas(params);
      setCampanas(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error('[ListaCampanas]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar las campañas.');
    } finally { setLoading(false); }
  }, [page, estado, q, orden, dir]);

  useEffect(() => { cargar(); }, [cargar]);

  function cambiarEstado(v) {
    setSp(paramsDeFiltrosLista(sp, { estado: v, q }));
  }
  function ordenarPor(columna) {
    setSp(paramsDeFiltrosLista(sp, { estado, q, ...siguienteOrden({ orden, dir }, columna) }));
  }
  function ordenarMovil(valor) {
    const [o, d] = (valor || '').split(':');
    setSp(paramsDeFiltrosLista(sp, { estado, q, orden: o || '', dir: d || '' }));
  }
  function quitarFiltros() {
    setTexto('');
    setSp(paramsDeFiltrosLista(sp, { estado: '', q: '' }));
  }

  // --- Borrar (Borrador / Activa / Pausada) ---
  function pedirBorrar(campana) {
    setBorrarError(null);
    setBorrarTarget({ id: campana.id, titulo: campana.titulo });
  }

  async function confirmarBorrar() {
    if (!borrarTarget) return;
    setBorrarError(null);
    try {
      await deleteCampana(borrarTarget.id);
      setBorrarTarget(null);
      if (campanas.length === 1 && page > 1) setPage(page - 1);
      else await cargar();
    } catch (e) {
      console.error('[deleteCampana]', e);
      setBorrarError(e?.response?.data?.error || 'No se pudo borrar la campaña.');
    }
  }

  function cancelarBorrar() {
    setBorrarTarget(null); setBorrarError(null);
  }

  // Recarga tras acciones sobre una campaña Cancelada (eliminar / reactivar).
  async function recargarLista() {
    if (campanas.length === 1 && page > 1) setPage(page - 1);
    else await cargar();
  }

  function handleCampanaReactivada(resp) {
    let msg = 'Campaña reactivada.';
    if (resp?.participaciones_revividas > 0) {
      msg += ` Se restauraron ${resp.participaciones_revividas} participaciones.`;
    }
    if (resp?.aviso) msg += ` ${resp.aviso}`;
    toast.success(msg);
    recargarLista();
  }

  const hayFiltros = !!(estado || q);
  const acciones = (c) => (
    <AccionesFila
      campana={c}
      permisos={permisosDe(c)}
      onVer={() => navigate(`/citas-tecnicas/campanas/${c.id}/resumen`)}
      onEditar={() => { const r = rutaEdicion(c); if (r) navigate(r); }}
      onPedirBorrar={() => pedirBorrar(c)}
      onReactivada={handleCampanaReactivada}
      onEliminadaCancelada={recargarLista}
    />
  );

  // Anchos en %: «Campaña» se lleva la parte mayor sin comerse la pantalla; el resto, lo justo.
  const columnas = [
    {
      // max-w-0: sin él, el nowrap del título ensancharía la columna en vez de cortarse con «…».
      key: 'titulo', header: 'Campaña', sortable: true, width: '36%', className: 'max-w-0',
      render: (c) => <TituloCampana c={c} unaLinea />,
    },
    {
      key: 'periodo', header: 'Periodo', sortable: true, width: '16%',
      render: (c) => <span className="whitespace-nowrap text-xs">{formatPeriodo(c.fecha_inicio, c.fecha_limite)}</span>,
    },
    {
      key: 'progreso', header: 'Progreso', sortable: true, width: '18%',
      render: (c) => <Progreso c={c} />,
    },
    {
      key: 'estado', header: 'Estado', sortable: true, width: '12%',
      render: (c) => <EstadoBadge c={c} />,
    },
    {
      key: 'acciones', header: 'Acciones', align: 'right', width: '18%',
      render: acciones,
    },
  ];

  const vacio = hayFiltros ? (
    <EmptyState
      variant="medium"
      title="Ninguna campaña coincide con los filtros."
      action={<Button type="button" variant="ghost" size="sm" onClick={quitarFiltros}>Quitar filtros</Button>}
    />
  ) : (
    <EmptyState
      variant="full"
      icon={FolderKanban}
      title="Aún no hay campañas"
      description={general.nueva ? 'Crea la primera para empezar a citar a las personas.' : 'Cuando se cree alguna, aparecerá aquí.'}
      action={general.nueva ? (
        <Button type="button" onClick={() => navigate('/citas-tecnicas/campanas/nueva')}>
          <Plus className="h-4 w-4" /> Nueva campaña
        </Button>
      ) : null}
    />
  );
  const paginacion = campanas.length > 0
    ? <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
    : null;

  return (
    <div className="min-h-full px-4 py-5 sm:px-6">
      <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold leading-tight text-v25-ink-900">Campañas</h1>
          <p className="mt-1 text-sm text-v25-ink-500">
            Gestión de campañas de intervención
            {!loading && !error && (
              <>
                {' · '}
                <span className="font-medium text-v25-ink-700">
                  {total} {total === 1 ? 'campaña' : 'campañas'}
                </span>
              </>
            )}
          </p>
        </div>

        {general.nueva && (
          <Button type="button" onClick={() => navigate('/citas-tecnicas/campanas/nueva')} className="shrink-0">
            <Plus className="h-4 w-4" />
            Nueva campaña
          </Button>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <ToggleGroup value={estado} onChange={cambiarEstado} className="flex-wrap">
          {ESTADOS_LISTA.map((opt) => (
            <ToggleItem key={opt.value || 'todas'} value={opt.value} label={opt.label} />
          ))}
        </ToggleGroup>
        <Input
          icon={Search}
          aria-label="Buscar campaña"
          placeholder="Buscar campaña…"
          value={texto}
          maxLength={MAX_BUSQUEDA}
          onChange={(e) => setTexto(e.target.value)}
          wrapperClassName="w-full sm:ml-auto sm:w-72"
        />
        {/* Sin cabeceras en las tarjetas: el orden, aquí */}
        <Select
          aria-label="Ordenar"
          value={orden ? `${orden}:${dir}` : ''}
          onChange={(e) => ordenarMovil(e.target.value)}
          wrapperClassName="w-full md:hidden"
        >
          {ORDENES_MOVIL.map((o) => <option key={o.value || 'defecto'} value={o.value}>{o.label}</option>)}
        </Select>
      </div>

      {loading ? (
        <EsqueletoTabla />
      ) : error ? (
        <div className="rounded-v25-lg border border-v25-line bg-white px-6 py-12 text-center" role="alert">
          <AlertCircle className="mx-auto mb-2 h-6 w-6 text-red-500" />
          <p className="mb-3 text-sm text-red-700">{error}</p>
          <Button type="button" variant="outline" size="sm" onClick={cargar}>Reintentar</Button>
        </div>
      ) : (
        <>
          {/* Desde md, la tabla */}
          <div className="hidden md:block" data-vista="tabla">
            <Table
              columns={columnas}
              rows={campanas}
              onSort={ordenarPor}
              sortBy={orden || undefined}
              sortDir={dir || undefined}
              onRowClick={(c) => navigate(rutaFila(c))}
              rowLabel={(c) => `${c.titulo} · ${etiquetaDe(ESTADO_CAMPANA, c.estado).label}`}
              emptyState={vacio}
              footer={paginacion}
            />
          </div>
          {/* En pantallas estrechas, tarjetas */}
          <div className="md:hidden" data-vista="tarjetas">
            {campanas.length === 0 ? (
              <div className="rounded-v25-lg border border-v25-line bg-white">{vacio}</div>
            ) : (
              <ul className="space-y-3">
                {campanas.map((c) => (
                  <TarjetaCampana key={c.id} c={c} onAbrir={() => navigate(rutaFila(c))} acciones={acciones(c)} />
                ))}
              </ul>
            )}
            {paginacion && <div className="mt-3 overflow-hidden rounded-v25-lg border border-v25-line bg-white">{paginacion}</div>}
          </div>
        </>
      )}

      {borrarTarget && (
        <ConfirmacionEliminar
          open={!!borrarTarget}
          titulo="Borrar campaña"
          descripcion={
            borrarError
              ? borrarError
              : `Vas a borrar la campaña "${borrarTarget.titulo}". Esta acción no se puede deshacer. Si la campaña tiene citas reservadas, primero debes cancelarla.`
          }
          palabraConfirmacion={borrarTarget.titulo}
          textoBotonConfirmar="Borrar definitivamente"
          onClose={cancelarBorrar}
          onConfirm={confirmarBorrar}
        />
      )}
    </div>
  );
}

// unaLinea (tabla): el título en una línea, cortado con «…» y entero en el tooltip, para que todas las
// filas midan lo mismo. En la tarjeta de móvil se parte en varias líneas.
function TituloCampana({ c, unaLinea = false }) {
  const usuarios = c.usuariosCount || 0;
  const tecnicos = c.tecnicosCount || 0;
  return (
    <div className="min-w-0">
      {unaLinea ? (
        <div className="truncate font-medium text-v25-ink-900" title={c.titulo}>{c.titulo}</div>
      ) : (
        <div className="font-medium text-v25-ink-900 break-words">{c.titulo}</div>
      )}
      <div className="text-xs text-v25-ink-500">
        {usuarios} {usuarios === 1 ? 'usuario' : 'usuarios'} · {tecnicos} {tecnicos === 1 ? 'técnico' : 'técnicos'}
      </div>
    </div>
  );
}

function Progreso({ c }) {
  const progreso = c.progreso || 0;
  return (
    <div className="flex items-center gap-2" aria-label={`${progreso}% completado`}>
      <ProgressBar value={progreso} max={100} className="w-24" />
      <span className="text-xs tabular-nums text-v25-ink-500">{progreso}%</span>
    </div>
  );
}

function EstadoBadge({ c }) {
  const e = etiquetaDe(ESTADO_CAMPANA, c.estado);
  return <Badge tone={e.tone}>{e.label}</Badge>;
}

// Tarjeta de móvil: el título es el botón (Tab y Enter); pulsar la tarjeta hace lo mismo.
function TarjetaCampana({ c, onAbrir, acciones }) {
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <li className="cursor-pointer rounded-v25-lg border border-v25-line bg-white p-4" onClick={onAbrir}>
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAbrir(); }}
          className="min-w-0 text-left focus-visible:outline-none focus-visible:shadow-v25-focus rounded-v25-sm"
        >
          <TituloCampana c={c} />
        </button>
        <EstadoBadge c={c} />
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 text-xs text-v25-ink-700">
        <span className="whitespace-nowrap">{formatPeriodo(c.fecha_inicio, c.fecha_limite)}</span>
        <Progreso c={c} />
      </div>
      <div className="mt-3 border-t border-v25-surface-raised pt-2">{acciones}</div>
    </li>
  );
}

function AccionesFila({ campana, permisos, onVer, onEditar, onPedirBorrar, onReactivada, onEliminadaCancelada }) {
  const esBorrador = campana.estado === 'Borrador';
  return (
    // La celda para el clic: los botones no abren la fila.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
      <BotonAccion label="Ver resumen" onClick={onVer} icon={<Eye className="h-4 w-4 text-v25-ink-500" />} />
      {permisos.editar && (
        <BotonAccion
          label={esBorrador ? 'Continuar borrador' : 'Editar'}
          onClick={onEditar}
          icon={<Pencil className="h-4 w-4 text-v25-ink-700" />}
        />
      )}
      {permisos.lapizDesactivado && (
        <button
          type="button"
          disabled
          aria-disabled="true"
          aria-label="No editable: la campaña ha terminado"
          title="No editable: la campaña ha terminado"
          className="grid h-8 w-8 cursor-not-allowed place-items-center rounded-v25-md"
        >
          <Pencil className="h-4 w-4 text-v25-ink-300" />
        </button>
      )}
      {permisos.borrar && (
        <BotonAccion
          label="Borrar"
          onClick={onPedirBorrar}
          icon={<Trash2 className="h-4 w-4 text-red-500" />}
          hoverClass="hover:bg-red-50"
        />
      )}
      {/* Fase 13 (Opción C) — solo en las canceladas y si hay algo que se pueda hacer; como iconos */}
      <AccionesCampanaCancelada
        campana={campana}
        comoIconos
        puedeReactivar={permisos.reactivar}
        puedeEliminar={permisos.eliminar}
        onReactivada={onReactivada}
        onEliminada={onEliminadaCancelada}
        eliminarCampana={deleteCampana}
        cambiarEstadoCampana={cambiarEstadoCampana}
        contarParticipacionesCanceladasSistema={contarParticipacionesCanceladasSistema}
      />
    </div>
  );
}

function BotonAccion({ label, onClick, icon, hoverClass = 'hover:bg-v25-surface-raised' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`grid h-8 w-8 place-items-center rounded-v25-md focus-visible:outline-none focus-visible:shadow-v25-focus ${hoverClass}`}
    >
      {icon}
    </button>
  );
}

function EsqueletoTabla() {
  return (
    <div className="overflow-hidden rounded-v25-lg border border-v25-line bg-white" aria-busy="true" aria-label="Cargando campañas">
      <div className="h-9 bg-v25-surface-sunken" />
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-6 border-t border-v25-surface-raised px-3 py-3.5">
          <div className="min-w-0 flex-1 space-y-1.5">
            <Skeleton className="h-4 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
          <Skeleton className="hidden h-3 w-24 sm:block" />
          <Skeleton className="hidden h-2 w-28 md:block" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-6 w-16" />
        </div>
      ))}
    </div>
  );
}
